import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import DeleteIcon from '@material-ui/icons/Delete'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import {connectQueryString} from 'containers/shared'
import { AllocationActions, SnackbarActions } from 'actionsets'
import Table, { TableBody, TableRow, TableCell } from 'components/CollapsibleTable'
import {FilterBar, IndexActions, SortedTableHead} from 'components'
import {Authorization} from 'utils'
import { Countdown, Pagination, ConfirmationDialog, ActionIndicator, SearchComboInput, DateTimePicker } from 'components'
import Checkbox from 'components/styled/Checkbox'
import Dependent from 'containers/shared/Dependent'
import AllocationForm from './AllocationForm'
import { DialogHandler, humanize } from 'utils'
import StyleSheet from 'styles'
import moment from 'moment'
import EditableLabel from 'components/EditableLabel'
import TextField from '@material-ui/core/TextField'
import {NumberDisplay, NumberField} from 'components/numbers'

export class AllocationsList extends Component {

  static propTypes = {
    dealSheet: PropTypes.object,
    allocations: PropTypes.array.isRequired,
    selection: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }

  static defaultProps = {
    pageSize: 10,
    page: 1,
  }

  constructor(props) {
    super(props)
    this.state = {
      showNewAllocationForm: false,
    }
    AllocationActions.bindActions(this)
    SnackbarActions.bindActions(this, 'snackbar')
    this.deleteConfirmationDialogHandler = new DialogHandler(this)
  }

  get selectedAllocations (){
    return Object.values(this.props.selection).filter(x => x)
  }

  get selectedAllocationCount(){
    return this.selectedAllocations.length
  }

  get hasAllSelected(){
    return this.props.allocations.every(({id}) => !!this.props.selection[id] )
  }

  get dealSheet(){
    return this.props.dealSheet
  }

  get sharedAttributes(){
    const sharedAttributes = {}
    const uniqueParams = {
      rate:   new Set(),
      amount:      new Set(),
      startDate:   new Set(),
      endDate:     new Set(),
      timezone: new Set(),
      coinPercentage: new Set(),
      tokenPercentage: new Set(),
      hiddenCommissions: false
    }
    this.selectedAllocations.forEach(({rate, amount, startDate, endDate, timezone, organisationCommission: { coinPercentage, tokenPercentage }}) => {
      uniqueParams.rate.add(rate)
      uniqueParams.amount.add(amount)
      uniqueParams.startDate.add(startDate)
      uniqueParams.endDate.add(endDate)
      uniqueParams.timezone.add(timezone)
      uniqueParams.coinPercentage.add(coinPercentage)
      uniqueParams.tokenPercentage.add(tokenPercentage)
    })
    Object.keys(uniqueParams).forEach(key => {
      sharedAttributes[key] = uniqueParams[key].size === 1 ? uniqueParams[key].values().next().value : undefined
    })

    sharedAttributes.organisationCommission = { coinPercentage: sharedAttributes.coinPercentage, tokenPercentage: sharedAttributes.tokenPercentage}
    delete sharedAttributes.coinPercentage
    delete sharedAttributes.tokenPercentage
    return sharedAttributes
  }

  get sharedParams(){
    return {...this.sharedAttributes }
  }

  get isCreateEditAllowed(){
    return this.dealSheet && Authorization.admin && ['approved', 'published', 'active'].includes(this.dealSheet.status)
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps && this.props.query !== prevProps.query) {
      this.loadData()
    }
  }

  dependsOn = () => {
    return this.loadData()
  }

  dependenciesMet = () => {
    return this.props.requests.filter(r => r.type === AllocationActions.INDEX).length === 0
  }

  loadData = () => {
    const { page, order, filter } = this.props
    const dealSheetId = (this.dealSheet) ? this.dealSheet.id : null

    return this.actions.index({
      dealSheetId,
      page, order, filter
    })
  }

  handleSelectToggle = allocation =>  {
    this.actions.setSelection({
      ...this.props.selection,
      [allocation.id]: this.props.selection[allocation.id] ? false : allocation
    })
  }

  handleToggleSelectAll = () => {
    const selection = {...this.props.selection}
    if(this.hasAllSelected){
      this.props.allocations.forEach((allocation) => selection[allocation.id] = false)
    }else{
      this.props.allocations.forEach((allocation) => selection[allocation.id] = allocation)
    }
    this.actions.setSelection(selection)
  }

  handleClearSelection = () => {
    this.actions.setSelection({})
  }

  showAllocation = allocation => {
    this.props.history.push(`/deal_sheets/${allocation.dealSheet.id}/allocations/${allocation.id}`)
  }

  deleteAllocation = async allocation => {
    this.setState({deletingAllocation: allocation})
    try {
      if (await this.deleteConfirmationDialogHandler.show() !== undefined) {
        await this.actions.destroy({id: allocation.id})
        this.loadData()
      }
    } finally {
      this.setState({deletingAllocation: undefined})
    }
  }

  showNewAllocationForm = () => {
    this.setState({showNewAllocationForm: true})
  }

  newAllocation = () => {
    this.actions.setNewInvestorIds({ids: [], params: null})
    this.actions.setEditAllocationIds({ids: [], params: null})
    this.showNewAllocationForm()
  }

  editAllocations = () => {
    const allocationIds = this.selectedAllocations.map(alloc => alloc.id)

    this.actions.setNewInvestorIds({ids: [], params: null})
    this.actions.setEditAllocationIds({ids: allocationIds, params: this.sharedParams})
    this.showNewAllocationForm()
  }

  handleTokensSentChange = allocation => ({target: {value}}) => {
    this.actions.update({id: allocation.id, tokensSent: value})
  }

  handleSaftToggle = allocation => async({target: {checked}}) => {
    try {
      await this.actions.update({id: allocation.id, saftComplete: checked})
      this.actions.snackbar.show("Allocation Updated")
    }
    catch (ex) {
      this.actions.snackbar.error("Could not update allocation")
    }
  }

  closeNewAllocationForm = (newAllocationsCreated) => {
    if(newAllocationsCreated){
      this.loadData()
    }
    this.setState({showNewAllocationForm: false})
  }

  renderSmallCheckbox = (allocation) => {
    return (
      <Checkbox small className={styles.smallCheckbox} checked={!!this.props.selection[allocation.id]} onClick={() => this.handleSelectToggle(allocation)}/>
    )
  }

  renderSaftCheckbox = (allocation) => {
    return (
      <Checkbox small className={styles.smallCheckbox} checked={!!allocation.saftComplete} onClick={this.handleSaftToggle(allocation)}/>
    )
  }

  renderAllocationFormButton = () => {
    const buttonLabel = (this.selectedAllocationCount === 0) ? 'Create' : 'Edit'
    const action = (this.selectedAllocationCount === 0) ? this.newAllocation : this.editAllocations

    return (
      <Fragment>
        {this.isCreateEditAllowed &&
          <Button variant='contained' color="primary" onClick={ action }>{ buttonLabel }</Button>
        }
        {this.state.showNewAllocationForm &&
          <AllocationForm onDone={ this.closeNewAllocationForm }/>
        }
      </Fragment>
    )
  }

  renderAllocation = (allocation, showTokenColumns, showPriceAtTime) => {
    const [isUtility, isEquity] = ['utility','equity','product'].map(t => allocation.dealSheet.typeOfIssuance === t)
    const { investorAccount, value, paymentReceived, amountClaimed, rate, ticker, currency, actionNeeded, tokensSent, dealSheet } = allocation
    const startDate = moment(allocation.startDate)
    const endDate   = moment(allocation.endDate)
    const now       = moment()

    let timeLeft = '-'
    if(startDate <= now && now <= endDate) {
      timeLeft = <Countdown until={endDate}/>
    } else if(now < startDate) {
      timeLeft = 'Not started'
    }

    return (
      <TableRow key={allocation.id} className={styles.row}>
        {this.isCreateEditAllowed &&
          <TableCell dontPrint mobileHeading={true}>{this.renderSmallCheckbox(allocation)}</TableCell>
        }
        <TableCell mobileHeading={true}><strong>{ investorAccount.accountName }</strong></TableCell>
        {!this.props.dealSheet &&
          <TableCell mobileLabel="Deal"><strong>{ dealSheet.name }</strong></TableCell>
        }
        <TableCell mobileLabel="Type">{ humanize(dealSheet.typeOfIssuance) }</TableCell>
        { showPriceAtTime && <TableCell mobileLabel='Price at time'>{isUtility && `${rate} ${ticker} / ${currency.code}`}</TableCell> }
        <TableCell mobileLabel='Allocation value'><NumberDisplay suffix={currency.code}>{value}</NumberDisplay></TableCell>
        <TableCell mobileLabel='Filled'><NumberDisplay suffix={currency.code}>{paymentReceived}</NumberDisplay></TableCell>
        <TableCell className={styles.timeLeftCell} mobileLabel='Time left'>{ timeLeft }</TableCell>
        { showTokenColumns && <TableCell mobileLabel='Tokens Claimed'>{!isEquity && <NumberDisplay suffix={ticker}>{amountClaimed}</NumberDisplay>}</TableCell> }
        { showTokenColumns && <TableCell mobileLabel='Token sent'>
            {!isEquity && <Fragment><EditableLabel
            classes={{input: styles.tokensSentInput}}
            value={ tokensSent || '0.0' }
            onChange={this.handleTokensSentChange(allocation)}
          /> {ticker}</Fragment>}
        </TableCell> }
        <TableCell className={styles.centeredCell} mobileLabel='Agreement'>
          {this.renderSaftCheckbox(allocation)}
        </TableCell>
        <TableCell dontPrint mobileHeading className={styles.centeredCell}>
          <ActionIndicator on={actionNeeded} />
        </TableCell>
        <TableCell dontPrint className={styles.actionButtons}>
          {
            this.isCreateEditAllowed &&
            <IconButton onClick={() => this.deleteAllocation(allocation)}>
              <DeleteIcon/>
            </IconButton>
          }
          <IconButton className={styles.iconButton} onClick={() => this.showAllocation(allocation)}>
            <ChevronRightIcon/>
          </IconButton>
        </TableCell>
      </TableRow>
    )
  }

  renderDeleteAllocationConfirmDialog = () => {
    const dialogProps = this.deleteConfirmationDialogHandler.mergedProps({})
    const message = (this.state.deletingAllocation) ?
      `Do you really want to delete ${this.state.deletingAllocation.investorAccount.userName}'s' allocation?` : ''

    return (
      <ConfirmationDialog title='Delete' {...dialogProps}>
        <Typography>{ message }</Typography>
      </ConfirmationDialog>
    )
  }

  render = () => {
    const { allocations, totalPages } = this.props
    const isEverythingSelected = !!(this.hasAllSelected && this.selectedAllocationCount)
    const showTokenColumns = allocations.some((a) => a.dealSheet.typeOfIssuance !== 'equity')
    const showPriceAtTime = allocations.some((a) => a.dealSheet.typeOfIssuance === 'utility')

    return (
      <Fragment>
        <FilterBar>
          <SearchComboInput searchTextMember="investorName">
            <TextField member='investorName' fullWidth/>
            <div>
              <Typography variant="subtitle1">Allocated value</Typography>
              <NumberField member='value.start' label="Min"/>
              <NumberField member='value.end' label="Max"/>
            </div>
            <div>
              <Typography variant="subtitle1">Allocated amount</Typography>
              <NumberField member='amount.start' label="Min"/>
              <NumberField member='amount.end' label="Max"/>
            </div>
            <div>
              <Typography variant="subtitle1">Start Date</Typography>
              <DateTimePicker member='startDate.start' label="From" showDate />
              <DateTimePicker member='startDate.end' label="To" showDate />
            </div>
            <div>
              <Typography variant="subtitle1">End Date</Typography>
              <DateTimePicker member='endDate.start' label="From" showDate />
              <DateTimePicker member='endDate.end' label="To" showDate />
            </div>
            <Typography className={styles.checkBox}><Checkbox type="checkbox" member="actionNeeded" />Action Needed</Typography>
          </SearchComboInput>
          <IndexActions exportLink={`/api${this.dealSheet ? `/deal_sheets/${this.dealSheet.id}` : ''}/allocations.csv`}/>
          { this.renderAllocationFormButton() }
        </FilterBar>

        <Table className={styles.root}>
          <SortedTableHead>
            <TableRow>
              {this.isCreateEditAllowed &&
                <TableCell dontPrint>
                  <Checkbox small className={styles.smallCheckbox} checked={isEverythingSelected} onChange={this.handleToggleSelectAll}/>
                </TableCell>
              }
              <TableCell orderBy="name">Investor</TableCell>
              {!this.props.dealSheet &&
                <TableCell orderBy="name">Deal</TableCell>
              }
              <TableCell orderBy="type">Type</TableCell>
              { showPriceAtTime && <TableCell orderBy="rate">Price at time</TableCell> }
              <TableCell orderBy="value">Allocation value</TableCell>
              <TableCell orderBy="paymentReceived">Filled</TableCell>
              <TableCell orderBy="endDate">Time left</TableCell>
              { showTokenColumns && <TableCell orderBy="amountClaimed">Claimed</TableCell> }
              { showTokenColumns && <TableCell orderBy="tokensSent">Token sent</TableCell> }
              <TableCell className={styles.centeredCell}>Agreement</TableCell>
              <TableCell dontPrint className={styles.centeredCell}>Action needed</TableCell>
              <TableCell dontPrint className={styles.actionButtons}>{/* right arrow */}</TableCell>
            </TableRow>
          </SortedTableHead>
          <TableBody>
            { allocations.map((a) => this.renderAllocation(a, showTokenColumns, showPriceAtTime)) }
          </TableBody>
        </Table>
        <Pagination totalPages={totalPages} showAll/>
        { this.renderDeleteAllocationConfirmDialog() }
      </Fragment>
    )
  }
}

const styles = StyleSheet({
  root: {

  },
  smallCheckbox: {
    margin: -19
  },
  row: {
    '& td': {
      height: '40px',
    }
  },
  tokensSentInput: {
    width: 50,
    '& input': {
      fontSize: '13px',
    }
  },
  iconButton: {
    width: '40px',
    height: '40px',
  },
  timeLeftCell: {
    width: '100px'
  },
  actionButtons: {
    whiteSpace: 'nowrap',
    width: 1,
    '& button': {
      width: 32,
      height: 32,
      padding: 0,
    }
  },
  centeredCell: {
    textAlign: 'center'
  },
  checkBox: {
    marginLeft: -13
  },
  pageActions: {
    flex: 1,
    textAlign: 'right',
    paddingRight: 15,
    margin: [-2, 0]
  }
}, {classNamePrefix: 'AllocationsList-'})

const dependentcomponent = connectQueryString(Dependent({loader: true})(AllocationsList))
export default connect(({allocations}) => { return { ...allocations } })(withRouter(dependentcomponent))